import { useState, useEffect, useCallback, useRef } from 'react';
import {
    FaBolt,
    FaClock,
    FaCalendarAlt,
    FaExchangeAlt,
    FaHandPointer,
    FaUserPlus,
    FaComment,
    FaRedo,
    FaExclamationTriangle,
    FaCheckCircle,
    FaTimesCircle,
    FaToggleOn,
    FaToggleOff,
    FaInfoCircle,
    FaSpinner,
    FaLayerGroup,
    FaBan,
    FaSyncAlt
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import * as Sentry from '@sentry/react';

import useSchedulerCountdown from '@/hooks/useSchedulerCountdown';

// ─── Constants ────────────────────────────────────────────────────────────────

const TRIGGER_TYPE_LABELS = {
    // Template workflow triggers
    DateField: 'Date-based trigger',
    OnCreate: 'On creation',
    OnStatusChange: 'On status change',
    OnFieldChange: 'On field change',
    OnAssignment: 'On assignment',
    OnComment: 'On comment',
    Recurring: 'Recurring schedule',
    OnOverdue: 'On overdue',
    Manual: 'Manual (run only when triggered)',
    // Board rule trigger types
    StatusChange: 'When item enters a column',
    TimeBasedAge: 'When item stays too long in a column',
    TimeBasedDeadline: 'When deadline is approaching',
    FieldChange: 'When a field value changes',
    OnPriorityChange: 'When priority changes',
};

const TRIGGER_ICONS = {
    DateField: FaCalendarAlt,
    OnCreate: FaBolt,
    OnStatusChange: FaExchangeAlt,
    OnFieldChange: FaExclamationTriangle,
    OnAssignment: FaUserPlus,
    OnComment: FaComment,
    Recurring: FaRedo,
    OnOverdue: FaClock,
    Manual: FaHandPointer,
    // Board rule trigger types
    StatusChange: FaExchangeAlt,
    TimeBasedAge: FaClock,
    TimeBasedDeadline: FaCalendarAlt,
    FieldChange: FaExclamationTriangle,
    OnPriorityChange: FaExclamationTriangle,
};

const STATUS_CLASSES = {
    Completed: 'status-badge--success',
    CompletedWithErrors: 'status-badge--warning',
    Failed: 'status-badge--error',
    Running: 'status-badge--info',
    Pending: 'status-badge--default'
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    try {
        return new Date(dateStr).toLocaleString(undefined, {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return dateStr;
    }
};

const formatRelative = (dateStr) => {
    if (!dateStr) return null;
    try {
        const diff = new Date(dateStr) - new Date();
        const absMin = Math.round(Math.abs(diff) / 60000);
        const absHour = Math.round(absMin / 60);
        const absDay = Math.round(absHour / 24);

        if (diff < 0) {
            if (absMin < 60) return `${absMin}m ago`;
            if (absHour < 24) return `${absHour}h ago`;
            return `${absDay}d ago`;
        } else {
            if (absMin < 60) return `in ${absMin}m`;
            if (absHour < 24) return `in ${absHour}h`;
            return `in ${absDay}d`;
        }
    } catch {
        return null;
    }
};

const describeDateFieldTrigger = (triggerConfig) => {
    if (!triggerConfig) return null;
    const { dateField, timeOfDay, offsetDays } = triggerConfig;
    if (!dateField) return null;
    const parts = [`at ${timeOfDay || '17:00'} on the '${dateField}' date`];
    if (offsetDays > 0) parts.push(`(+ ${offsetDays} day${offsetDays !== 1 ? 's' : ''})`);
    if (offsetDays < 0) parts.push(`(${offsetDays} day${offsetDays !== -1 ? 's' : ''})`);
    return 'Fires ' + parts.join(' ');
};

// ─── Hook: live countdown to next automation run ─────────────────────────────

// const useSchedulerCountdown = (apiFetch) => {
//     const [nextRunAt, setNextRunAt] = useState(null);
//     const [secondsRemaining, setSecondsRemaining] = useState(null);
//     const [intervalSec, setIntervalSec] = useState(300);
//     const timerRef = useRef(null);

//     const fetchStatus = useCallback(async () => {
//         try {
//             const res = await apiFetch('/api/WorkflowAutomations/scheduler-status');
//             const data = await res.json();
//             if (data.success && data.data?.nextRunAtUtc) {
//                 const raw = data.data.nextRunAtUtc;
//                 const utcStr = typeof raw === 'string' && !raw.endsWith('Z') ? raw + 'Z' : raw;
//                 setNextRunAt(new Date(utcStr));
//                 if (data.data.intervalSeconds) setIntervalSec(data.data.intervalSeconds);
//             }
//         } catch {
//             Non-critical — timer just won't display
//         }
//     }, [apiFetch]);

//     useEffect(() => {
//         fetchStatus();
//     }, [fetchStatus]);

//     useEffect(() => {
//         if (!nextRunAt) return;

//         const tick = () => {
//             const diff = Math.max(0, Math.round((nextRunAt.getTime() - Date.now()) / 1000));
//             setSecondsRemaining(diff);

//             When countdown hits zero, re-fetch after a short delay to get the new next-run time
//             if (diff === 0) {
//                 setTimeout(fetchStatus, 3000);
//             }
//         };

//         tick();
//         timerRef.current = setInterval(tick, 1000);
//         return () => clearInterval(timerRef.current);
//     }, [nextRunAt, fetchStatus]);

//     const formatCountdown = (secs) => {
//         if (secs == null) return null;
//         const m = Math.floor(secs / 60);
//         const s = secs % 60;
//         return `${m}:${s.toString().padStart(2, '0')}`;
//     };

//     return {
//         secondsRemaining,
//         formatted: formatCountdown(secondsRemaining),
//         intervalSec,
//         isRunningNow: secondsRemaining === 0
//     };
// };

// ─── Sub-component: Single automation card ───────────────────────────────────

const AutomationCard = ({ automation, isReadOnly, onOverrideChange }) => {
    const { apiFetch } = useAuth();

    const [showNoteInput, setShowNoteInput] = useState(false);
    const [noteText, setNoteText] = useState(automation.overrideNotes || '');
    const [isSaving, setIsSaving] = useState(false);

    const isCurrentlyEnabled = automation.effectiveIsActive;
    const hasOverride = automation.overrideState !== 'default';
    const isBoardRule = automation.automationSource === 'board';

    const TriggerIcon = TRIGGER_ICONS[automation.triggerType] || FaBolt;

    const handleToggle = () => {
        if (isReadOnly) return;
        if (hasOverride) {
            handleClearOverride();
        } else {
            setShowNoteInput(true);
        }
    };

    const handleSaveOverride = async () => {
        setIsSaving(true);
        try {
            const newEnabled = !isCurrentlyEnabled;
            const url = isBoardRule
                ? `/api/WorkflowAutomations/board-rule/${automation.id}/item-override/${automation._actionItemId}`
                : `/api/WorkflowAutomations/${automation.id}/item-override/${automation._actionItemId}`;

            const res = await apiFetch(url, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ isEnabled: newEnabled, notes: noteText.trim() || null })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message);
                setShowNoteInput(false);
                onOverrideChange?.();
            } else {
                toast.error(data.message || 'Failed to update automation');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to update automation');
        } finally {
            setIsSaving(false);
        }
    };

    const handleClearOverride = async () => {
        setIsSaving(true);
        try {
            const url = isBoardRule
                ? `/api/WorkflowAutomations/board-rule/${automation.id}/item-override/${automation._actionItemId}`
                : `/api/WorkflowAutomations/${automation.id}/item-override/${automation._actionItemId}`;

            const res = await apiFetch(url, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                toast.success('Override cleared — automation reverted to its global state');
                setShowNoteInput(false);
                onOverrideChange?.();
            } else {
                toast.error(data.message || 'Failed to clear override');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to clear override');
        } finally {
            setIsSaving(false);
        }
    };

    const lastRunStatusClass = STATUS_CLASSES[automation.lastRun?.status] || 'status-badge--default';

    return (
        <div className={`automation-card ${!isCurrentlyEnabled ? 'automation-card--disabled' : ''}`}>

            {/* ── Card header ────────────────────────────────────────────────── */}
            <div className='automation-card__header'>
                <div className='automation-card__icon-wrap'>
                    <TriggerIcon size={16} className='automation-card__trigger-icon' />
                </div>

                <div className='automation-card__title-group'>
                    <span className='automation-card__name'>
                        {automation.name}

                        {/* Board rule source badge */}
                        {isBoardRule && (
                            <span className='automation-card__badge automation-card__badge--inactive'
                                title='Configured in Board Settings → Automation Rules'>
                                Board rule
                            </span>
                        )}

                        {/* Global inactive badge — only when not force-enabled by override */}
                        {!automation.isActive && automation.overrideState !== 'enabled' && (
                            <span className='automation-card__badge automation-card__badge--inactive'
                                title='This automation is globally disabled'>
                                Globally off
                            </span>
                        )}

                        {/* Override badges */}
                        {automation.overrideState === 'disabled' && (
                            <span className='automation-card__badge automation-card__badge--suppressed'
                                title={automation.overrideNotes || 'Suppressed for this item'}>
                                <FaBan size={10} /> Suppressed for this item
                            </span>
                        )}
                        {automation.overrideState === 'enabled' && (
                            <span className='automation-card__badge automation-card__badge--override'
                                title={automation.overrideNotes || 'Manually enabled for this item'}>
                                <FaCheckCircle size={10} /> Enabled for this item
                            </span>
                        )}
                    </span>

                    <span className='automation-card__trigger-label'>
                        {TRIGGER_TYPE_LABELS[automation.triggerType] || automation.triggerType}

                        {/* DateField trigger detail (template automations) */}
                        {automation.triggerType === 'DateField' && automation.triggerConfig &&
                            describeDateFieldTrigger(automation.triggerConfig) && (
                                <span className='automation-card__trigger-detail'>
                                    {' — '}{describeDateFieldTrigger(automation.triggerConfig)}
                                </span>
                            )
                        }

                        {/* Column flow detail (board rules) */}
                        {isBoardRule && (automation.sourceColumnName || automation.destinationColumnName) && (
                            <span className='automation-card__trigger-detail'>
                                {automation.sourceColumnName && automation.destinationColumnName
                                    ? ` — "${automation.sourceColumnName}" → "${automation.destinationColumnName}"`
                                    : automation.destinationColumnName
                                        ? ` — moves to "${automation.destinationColumnName}"`
                                        : ` — from "${automation.sourceColumnName}"`}
                            </span>
                        )}
                    </span>
                </div>

                {/* ── Toggle — fully interactive for both template automations and board rules ── */}
                <div className='automation-card__toggle-wrap'
                    title={
                        isReadOnly
                            ? 'You do not have permission to change this'
                            : hasOverride
                                ? 'Click to clear override'
                                : isCurrentlyEnabled
                                    ? 'Click to suppress for this item'
                                    : 'Click to enable for this item'
                    }
                >
                    {isSaving ? (
                        <FaSpinner size={18} className='automation-card__spinner' />
                    ) : isCurrentlyEnabled ? (
                        <FaToggleOn size={22} className={`automation-card__toggle automation-card__toggle--on  ${isReadOnly ? 'automation-card__toggle--readonly' : ''}`} onClick={handleToggle} />
                    ) : (
                        <FaToggleOff size={22} className={`automation-card__toggle automation-card__toggle--off ${isReadOnly ? 'automation-card__toggle--readonly' : ''}`} onClick={handleToggle} />
                    )}
                </div>
            </div>

            {/* ── Description ────────────────────────────────────────────────── */}
            {automation.description && (
                <p className='automation-card__description'>{automation.description}</p>
            )}

            {/* ── Steps summary (template automations only) ──────────────────── */}
            {automation.steps?.length > 0 && (
                <div className='automation-card__steps'>
                    <span className='automation-card__steps-label'>
                        <FaLayerGroup size={11} /> {automation.steps.length} step{automation.steps.length !== 1 ? 's' : ''}:
                    </span>
                    <ol className='automation-card__step-list'>
                        {automation.steps.map((step, idx) => (
                            <li key={step.id ?? idx} className={`automation-card__step ${!step.isActive ? 'automation-card__step--inactive' : ''}`}>
                                <span className='automation-card__step-name'>{step.name}</span>
                                {step.description && (
                                    <span className='automation-card__step-desc'>{step.description}</span>
                                )}
                                {!step.isActive && (
                                    <span className='automation-card__step-badge'>disabled</span>
                                )}
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            {/* ── Execution info row ──────────────────────────────────────────── */}
            <div className='automation-card__meta-row'>
                <div className='automation-card__meta-item'>
                    {automation.lastRun ? (
                        <>
                            <span className={`status-badge ${lastRunStatusClass}`}>
                                {automation.lastRun.status === 'Completed' && <FaCheckCircle size={10} />}
                                {automation.lastRun.status === 'CompletedWithErrors' && <FaExclamationTriangle size={10} />}
                                {automation.lastRun.status === 'Failed' && <FaTimesCircle size={10} />}
                                {' '}{automation.lastRun.status}
                            </span>
                            <span className='automation-card__meta-date' title={formatDateTime(automation.lastRun.startedAt)}>
                                Last run: {formatRelative(automation.lastRun.startedAt) || formatDateTime(automation.lastRun.startedAt)}
                            </span>
                        </>
                    ) : (
                        <span className='automation-card__meta-date automation-card__meta-date--never'>
                            {isBoardRule ? 'Execution history not tracked here' : 'Never executed'}
                        </span>
                    )}
                </div>

                {automation.nextRunEta && (
                    <div className='automation-card__meta-item automation-card__eta'>
                        <FaClock size={11} />
                        <span title={`Scheduled: ${formatDateTime(automation.nextRunEta)}`}>
                            ETA: {formatRelative(automation.nextRunEta) || formatDateTime(automation.nextRunEta)}
                        </span>
                        <span className='automation-card__eta-abs'>
                            ({formatDateTime(automation.nextRunEta)})
                        </span>
                    </div>
                )}
            </div>

            {/* ── Inline note input (shown after clicking the toggle) ─────────── */}
            {showNoteInput && !isReadOnly && (
                <div className='automation-card__note-row'>
                    <FaInfoCircle size={13} className='automation-card__note-icon' />
                    <input
                        type='text'
                        className='automation-card__note-input'
                        placeholder='Optional: reason for this change (saved in audit log)'
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        maxLength={1000}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveOverride();
                            if (e.key === 'Escape') setShowNoteInput(false);
                        }}
                    />
                    <button
                        className='automation-card__note-save btn-sm btn-primary'
                        onClick={handleSaveOverride}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving…' : (!isCurrentlyEnabled ? 'Enable' : 'Suppress')}
                    </button>
                    <button
                        className='automation-card__note-cancel btn-sm'
                        onClick={() => setShowNoteInput(false)}
                        disabled={isSaving}
                    >
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
};

// ─── Main component ───────────────────────────────────────────────────────────

const ActionItemAutomationsTab = ({ actionItemId, isReadOnly = false, onCountChange }) => {
    const { apiFetch, hasPermission } = useAuth();
    const isAdmin = hasPermission('Admin');

    const [automations, setAutomations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isRunning, setIsRunning] = useState(false);

    const scheduler = useSchedulerCountdown(apiFetch);

    const fetchAutomations = useCallback(async () => {
        if (!actionItemId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await apiFetch(`/api/WorkflowAutomations/for-action-item/${actionItemId}`);
            const data = await res.json();
            if (data.success) {
                const enriched = (data.data ?? []).map(a => ({ ...a, _actionItemId: actionItemId }));
                setAutomations(enriched);
                onCountChange?.(enriched.length);
            } else {
                setError(data.message || 'Failed to load automations');
            }
        } catch (err) {
            Sentry.captureException(err);
            setError('Failed to load automations');
        } finally {
            setLoading(false);
        }
    }, [actionItemId, apiFetch, onCountChange]);

    const handleRunForItem = async () => {
        if (isRunning) return;
        setIsRunning(true);
        try {
            const res = await apiFetch(`/api/WorkflowAutomations/item/${actionItemId}/run-all`, {
                method: 'POST'
            });
            const data = await res.json();
            if (data.success) {
                toast.success(data.message || 'Automations triggered for this item');
                await fetchAutomations(); // refresh last-run timestamps
            } else {
                toast.error(data.message || 'Failed to trigger automations');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Failed to trigger automations');
        } finally {
            setIsRunning(false);
        }
    };

    useEffect(() => {
        fetchAutomations();
    }, [fetchAutomations]);

    if (loading) {
        return (
            <div className='automations-tab automations-tab--loading'>
                <FaSpinner size={18} className='automations-tab__spinner' />
                <span>Loading automations…</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className='automations-tab automations-tab--error'>
                <FaTimesCircle size={16} />
                <span>{error}</span>
            </div>
        );
    }

    if (automations.length === 0) {
        return (
            <div className='automations-tab automations-tab--empty'>
                <FaBolt size={24} className='automations-tab__empty-icon' />
                <p>No automations are configured for this action item.</p>
                <p className='automations-tab__empty-hint'>
                    Template automations can be added via the Template Designer in Settings.
                    Board automations can be added via Board Settings → Automation Rules.
                </p>
            </div>
        );
    }

    const templateCount = automations.filter(a => a.automationSource === 'template').length;
    const boardCount = automations.filter(a => a.automationSource === 'board').length;

    return (
        <div className='automations-tab'>
            <div className='automations-tab__header'>
                <span className='automations-tab__count'>
                    {templateCount > 0 && (
                        <span>{templateCount} template automation{templateCount !== 1 ? 's' : ''}</span>
                    )}
                    {templateCount > 0 && boardCount > 0 && (
                        <span style={{ margin: '0 6px', color: '#cbd5e1' }}>·</span>
                    )}
                    {boardCount > 0 && (
                        <span>{boardCount} board rule{boardCount !== 1 ? 's' : ''}</span>
                    )}
                </span>

                <div className='automations-tab__right-group'>
                    {scheduler.formatted && (
                        <span className={`automations-tab__timer ${scheduler.isRunningNow ? 'automations-tab__timer--running' : ''}`}
                            title='Time until next automation evaluation cycle'>
                            {scheduler.isRunningNow ? (
                                <><FaSyncAlt size={11} className='automations-tab__timer-spin' /> Running now…</>
                            ) : (
                                <><FaClock size={11} /> Next run in {scheduler.formatted}</>
                            )}
                        </span>
                    )}
                    {/* Run automations button — Admin only, only shown when board rules are present */}
                    {boardCount > 0 && (
                        <button
                            type="button"
                            className="btn-run-automations btn-run-automations--item"
                            onClick={handleRunForItem}
                            disabled={isRunning}
                            title="Manually trigger all board automation rules for this item now"
                        >
                            {isRunning
                                ? <><FaSpinner className="btn-run-automations__spin" /> Running…</>
                                : <><FaBolt /> Run automations</>
                            }
                        </button>
                    )}
                    {!isReadOnly && (
                        <span className='automations-tab__hint'>
                            <FaInfoCircle size={11} /> Toggles affect this item only
                        </span>
                    )}
                </div>
            </div>

            <div className='automations-tab__list'>
                {automations.map(automation => (
                    <AutomationCard
                        key={`${automation.automationSource}-${automation.id}`}
                        automation={automation}
                        isReadOnly={isReadOnly}
                        onOverrideChange={fetchAutomations}
                    />
                ))}
            </div>
        </div>
    );
};

export default ActionItemAutomationsTab;